import { useNavigate } from 'react-router-dom';
import { FiChevronRight } from 'react-icons/fi';
import AppLayout from '../components/layout/AppLayout';
import Button from '../components/common/Button';
import PageHeader from '../components/common/PageHeader';
import GettingStartedCard from '../components/dashboard/GettingStartedCard';
import AskVeeCard from '../components/dashboard/AskVeeCard';
import NextStepsFeed from '../components/dashboard/NextStepsFeed';
import RecentChats from '../components/dashboard/RecentChats';
import KpiGrid from '../components/dashboard/KpiGrid';
import PortfolioByType from '../components/dashboard/PortfolioByType';
import CriticalAlerts from '../components/dashboard/CriticalAlerts';
import UpsiMailboxBanner from '../components/dashboard/UpsiMailboxBanner';
import AccessRequestsBanner from '../components/dashboard/AccessRequestsBanner';
import QuickActions from '../components/dashboard/QuickActions';
import FilingProgress from '../components/dashboard/FilingProgress';
import { Icon } from '../components/common/Icon';
import { ROUTES } from '../constants/constants';
import { useDashboard } from '../hooks/useDashboard';
import { useApp } from '../context/AppContext';
import { useChat } from '../context/ChatContext';

/**
 * DashboardProPage — the compliance command centre. Composition only: every
 * section lives in components/dashboard/, all numbers come from the deadline
 * engine via useDashboard(). Nothing on this page invents a compliance fact.
 */
export default function DashboardProPage() {
  const navigate = useNavigate();
  const { stats, alerts, filingProgress, portfolio } = useDashboard();
  const { companies, activeCompany } = useApp();
  const { sendTurn, resetChat, consentPersist, consentTrain } = useChat();

  const totalCompanies = companies.length;

  // Ask Vee from the dashboard: start a FRESH conversation (Notion-Home
  // pattern — asking from home opens a new thread), fire the turn through the
  // provider, and land on /agent with the reply already streaming. The
  // previous chat stays in History.
  const askVee = (question) => {
    const q = String(question || '').trim();
    if (!q) return;
    resetChat();
    const userMsg = { role: 'user', content: q };
    sendTurn({
      userMsg,
      history: [userMsg],
      context: { platform: 'complyv', activeCompanyCin: activeCompany?.cin || null, webSearch: false },
      consent: { persist: consentPersist, train: consentTrain },
      sessionId: null,
      userText: q,
    });
    navigate(ROUTES.AGENT);
  };

  // Suggested prompts — seeded from the deadline engine's top alerts (real
  // companies, real forms), padded with evergreen asks. Deterministic text;
  // Vee only comes in when the user clicks.
  const suggestions = [
    ...alerts.slice(0, 2).map((a) =>
      a.daysOverdue < 0
        ? `${a.formCode} for ${a.companyName} is overdue — what do I do now?`
        : `What happens if ${a.companyName} misses ${a.formCode}?`),
    ...(totalCompanies > 0 ? ['Check my deadlines'] : []),
    'Draft a board resolution',
  ].slice(0, 4);

  // Deterministic next steps: AGM setup first (it makes every other date
  // exact), then the top engine alerts. Capped at 3 rows.
  const nextSteps = [
    ...((stats.pendingSetup ?? 0) > 0 ? [{
      id: 'agm-setup',
      tone: 'warn',
      title: `Set AGM dates for ${stats.pendingSetup} compan${stats.pendingSetup === 1 ? 'y' : 'ies'}`,
      why: 'Turns estimated deadlines into exact ones',
      badge: 'Setup',
      doLabel: 'Set dates',
      onDo: () => navigate(ROUTES.COMPANIES),
      askPrompt: 'Why does the AGM date matter for my filing deadlines?',
    }] : []),
    ...alerts.slice(0, (stats.pendingSetup ?? 0) > 0 ? 2 : 3).map((a) => ({
      id: `${a.companyName}-${a.formCode}`,
      tone: a.priority === 'critical' ? 'crit' : 'warn',
      title: `${a.formCode} — ${a.companyName}`,
      why: a.item,
      badge: a.daysOverdue < 0 ? `Overdue ${Math.abs(a.daysOverdue)}d` : `${a.daysOverdue}d left`,
      doLabel: 'View alert',
      onDo: () => navigate(ROUTES.ALERTS),
      askPrompt: a.daysOverdue < 0
        ? `${a.formCode} for ${a.companyName} is ${Math.abs(a.daysOverdue)} days overdue. What's the penalty and how do I fix this?`
        : `Walk me through filing ${a.formCode} for ${a.companyName} — it's due in ${a.daysOverdue} days.`,
    })),
  ].slice(0, 3);
  const fullyCompliant = stats.fullyCompliant ?? 0;
  const dueThisMonth = stats.dueThisMonth ?? 0;
  const overdueCritical = stats.overdueCritical ?? 0;
  const pendingSetup = stats.pendingSetup ?? 0;
  // Counted separately by the API, and kept separate here for the same reason.
  const ruleBook = stats.ruleBook || null;
  const compliantPct = totalCompanies > 0 ? Math.round((fullyCompliant / totalCompanies) * 100) : 0;
  const totalPenalty = portfolio?.alerts?.reduce((sum, a) => sum + (a.penalty || 0), 0) || 0;
  const penaltyDisplay = totalPenalty >= 100000 ? `₹${(totalPenalty / 100000).toFixed(1)}L` : `₹${totalPenalty.toLocaleString('en-IN')}`;

  return (
    <AppLayout pageTitle="Dashboard" hasDashboardAlert={overdueCritical > 0}>
      <div className="w-full max-w-[1320px] mx-auto">
        <PageHeader
          className="!mb-4"
          title="Compliance Command Centre"
          subtitle={`Managing ${totalCompanies} compan${totalCompanies === 1 ? 'y' : 'ies'} · All entity types · Live deadline tracking`}
        />

        {/* Pending Setup Banner */}
        {pendingSetup > 0 && (
          <div
            className="flex items-center gap-3.5 px-4 py-3 mb-3 rounded-[12px] border"
            style={{ background: '#faf6eb', borderColor: '#ebdcb9', boxShadow: '0 2px 8px rgba(122,112,96,0.02)' }}
          >
            <Icon name="warning" size={18} style={{ color: '#8a6a1f' }} />
            <div className="flex-1">
              <div className="text-[12px] font-bold" style={{ color: '#8a6a1f' }}>
                {pendingSetup} compan{pendingSetup === 1 ? 'y needs' : 'ies need'} AGM date setup
              </div>
              <div className="text-[10.5px] text-[#7a7060] mt-[1px]">
                Deadlines are estimated based on statutory AGM deadline. Set the actual AGM date for accurate tracking.
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="group shrink-0 whitespace-nowrap !py-1 !px-2.5 text-[11px] font-semibold hover:!bg-[#8a6a1f]/[0.08] hover:!text-[#8a6a1f]"
              rightIcon={<FiChevronRight size={12} className="transition-transform duration-200 group-hover:translate-x-0.5" />}
              onClick={() => navigate(ROUTES.COMPANIES)}
            >
              <span className="transition-transform duration-200 group-hover:translate-x-0.5">Set AGM Dates</span>
            </Button>
          </div>
        )}

        {/* A professional's requests to join clients' companies: pending ones
            wait on the owner, and this is the page they come back to. */}
        <AccessRequestsBanner />

        {/* Listed companies the user owns with no UPSI mailbox — the owner is
            the only one who can connect it, so only the owner is told. */}
        <UpsiMailboxBanner />

        {/* Rule-book obligations beyond the Companies Act forms above.
            Shown as its own line rather than folded into the KPI cards: those
            numbers mean "Companies Act filings" today, and quietly changing
            what they count is how a figure the client already reads stops
            meaning what they think it means. */}
        {ruleBook && (ruleBook.overdue > 0 || ruleBook.dueSoon > 0 || ruleBook.cannotDetermine > 0) && (
          <div
            className="flex items-center gap-3.5 px-4 py-3 mb-3 rounded-[12px] border"
            style={{ background: '#f4f8f8', borderColor: '#cfe3e1' }}
          >
            <Icon name="shield" size={18} style={{ color: '#145f5f' }} />
            <div className="flex-1">
              <div className="text-[12px] font-bold" style={{ color: '#145f5f' }}>
                {[
                  ruleBook.overdue > 0 && `${ruleBook.overdue} overdue`,
                  ruleBook.dueSoon > 0 && `${ruleBook.dueSoon} due soon`,
                ].filter(Boolean).join(' · ') || 'Beyond the Companies Act'}
                {' '}across labour, tax and state law
              </div>
              <div className="text-[10.5px] text-[#7a7060] mt-[1px]">
                From the compliance rule book, alongside the filings above.
                {ruleBook.cannotDetermine > 0
                  && ` ${ruleBook.cannotDetermine} more cannot be decided until the company profiles are filled in.`}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="group shrink-0 whitespace-nowrap !py-1 !px-2.5 text-[11px] font-semibold hover:!bg-[#145f5f]/[0.08] hover:!text-[#145f5f]"
              rightIcon={<FiChevronRight size={12} className="transition-transform duration-200 group-hover:translate-x-0.5" />}
              onClick={() => navigate(ROUTES.COMPLIANCE)}
            >
              <span className="transition-transform duration-200 group-hover:translate-x-0.5">View compliance</span>
            </Button>
          </div>
        )}

        {/* Role-aware onboarding runway (self-hides when dismissed/irrelevant) */}
        <GettingStartedCard />

        {/* Agentic entry point — ask Vee from home, land in the chat mid-stream */}
        <AskVeeCard suggestions={suggestions} onAsk={askVee} />

        <KpiGrid
          fullyCompliant={fullyCompliant}
          compliantPct={compliantPct}
          dueThisMonth={dueThisMonth}
          overdueCritical={overdueCritical}
          penaltyDisplay={penaltyDisplay}
          totalPenalty={totalPenalty}
          totalCompanies={totalCompanies}
        />

        <PortfolioByType companies={companies} onDetailedView={() => navigate(ROUTES.COMPANY_TYPES)} />

        <div className="grid grid-cols-1 xl:grid-cols-[2fr_1fr] gap-3.5">
          <div>
            <CriticalAlerts alerts={alerts} totalCompanies={totalCompanies} onOpenAlerts={() => navigate(ROUTES.ALERTS)} />
            <RecentChats />
          </div>
          <div className="flex flex-col gap-3.5">
            <NextStepsFeed steps={nextSteps} onAskVee={askVee} />
            <QuickActions />
            <FilingProgress filingProgress={filingProgress} />
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
